// Manifest + peaks loaders for the share-web SPA.
//
// Fetches the share manifest from `<cdnBase>/shares/<shareId>/manifest.json`
// and the peaks payload from `<cdnBase>/shares/<shareId>/<peaksUrl>`.
// Throws on non-2xx responses or unsupported manifest version.

import type {
  ShareManifest,
  SummaryPeaks,
  PerTrackSummaryPeaks,
} from '@bandway/share-types';

function joinUrl(base: string, ...segments: string[]): string {
  const trimmedBase = base.replace(/\/+$/, '');
  const tail = segments
    .map((seg) => seg.replace(/^\/+/, '').replace(/\/+$/, ''))
    .filter((seg) => seg.length > 0)
    .join('/');
  return `${trimmedBase}/${tail}`;
}

async function fetchJson<T>(url: string, label: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`${label} fetch failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

/**
 * Fetches and validates a share manifest directly from CDN (legacy path).
 *
 * Throws `Error("manifest fetch failed: <status>")` on non-2xx and
 * `Error("unsupported manifest version: <v>")` if `v !== 1`.
 *
 * @deprecated Use `loadManifest(shareId, apiBase)` for gated shares.
 * This function is kept for backward compat with existing callers.
 */
export async function loadManifestDirect(
  cdnBase: string,
  shareId: string,
): Promise<ShareManifest> {
  const url = joinUrl(cdnBase, 'shares', shareId, 'manifest.json');
  const parsed = await fetchJson<ShareManifest>(url, 'manifest');
  if (parsed.v !== 1 && parsed.v !== 2 && parsed.v !== 3) {
    throw new Error(`unsupported manifest version: ${parsed.v}`);
  }
  return parsed;
}

// ---------------------------------------------------------------------------
// Gated manifest loader — fetches via the worker gate with auth cookie.
// ---------------------------------------------------------------------------

export type ManifestLoadResult =
  | { state: 'ok'; manifest: any; playbackToken: string | null }
  | { state: 'needs-auth'; teamName: string | null }
  | { state: 'revoked' }
  | { state: 'error'; status: number };

/**
 * Fetches the manifest through the worker gate (`/api/shares/:id/manifest.json`).
 * Sends `credentials: 'include'` so the `bw_session` cookie is forwarded.
 *
 * Returns a discriminated union:
 *  - `{ state: 'ok', manifest, playbackToken }` on 200
 *  - `{ state: 'needs-auth', teamName }` on 401
 *  - `{ state: 'revoked' }` on 403
 *  - `{ state: 'error', status }` on any other non-2xx
 */
export async function loadManifest(shareId: string, apiBase = ''): Promise<ManifestLoadResult> {
  const res = await fetch(`${apiBase}/api/shares/${encodeURIComponent(shareId)}/manifest.json`, {
    credentials: 'include',
  });
  if (res.status === 401) {
    const b = await res.json().catch(() => ({}));
    return { state: 'needs-auth', teamName: (b as any)?.teamName ?? null };
  }
  if (res.status === 403) return { state: 'revoked' };
  if (!res.ok) return { state: 'error', status: res.status };
  const manifest = await res.json();
  return { state: 'ok', manifest, playbackToken: res.headers.get('X-Playback-Token') };
}

/**
 * Fetches the per-track summary peaks JSON pointed to by a v2 manifest's
 * `perTrackPeaksUrl`. Returns `null` for v1 manifests, when the URL is
 * missing, or when the fetch fails (graceful degrade — the SPA falls back
 * to placeholder waveforms in that case).
 */
export async function loadPerTrackPeaks(
  cdnBase: string,
  shareId: string,
  manifest: ShareManifest,
): Promise<PerTrackSummaryPeaks | null> {
  if (manifest.v < 2 || !manifest.perTrackPeaksUrl) {
    return null;
  }
  const url = joinUrl(cdnBase, 'shares', shareId, manifest.perTrackPeaksUrl);
  try {
    return await fetchJson<PerTrackSummaryPeaks>(url, 'peaks-per-track');
  } catch {
    return null;
  }
}

/**
 * Fetches the summary peaks JSON pointed to by a manifest's `peaksUrl`.
 *
 * The manifest stores `peaksUrl` as a relative path so the share bundle can
 * version the peaks file independently — we just resolve it under the share's
 * CDN folder.
 *
 * Throws `Error("peaks fetch failed: <status>")` on non-2xx.
 */
export async function loadPeaks(
  cdnBase: string,
  shareId: string,
  peaksUrl: string,
): Promise<SummaryPeaks> {
  const url = joinUrl(cdnBase, 'shares', shareId, peaksUrl);
  return fetchJson<SummaryPeaks>(url, 'peaks');
}
